<template>
    <tr :class="['staff-table-row', { 'staff-table-row_editing': editing }]" @dblclick.prevent="openRow">
        <td>
            <div>
                <input
                    :id="`input-assignable-${staffMember.user_id}`"
                    type="checkbox"
                    :class="{ checked: staffMember.assignable }"
                />
                <label :for="`input-assignable-${staffMember.user_id}`"></label>
            </div>
        </td>
        <td :title="staffMember.first_name" v-text="staffMember.first_name"></td>
        <td>
            <div>
                <input
                    :id="`inputTwo-${staffMember.user_id}`"
                    type="checkbox"
                    :class="{ checked: staffMember.login_name && staffMember.isAAPrimary }"
                />
                <label :for="`inputTwo-${staffMember.user_id}`"></label>
            </div>
        </td>
        <td :title="staffMember.call_id" v-text="staffMember.call_id"></td>
        <td :title="staffMember.card_id" v-text="staffMember.card_id"></td>
        <td
            :class="[
                'staff-table-access-modules',
                { 'staff-table-access-modules_empty': staffMember.accessModules.length === 0 },
            ]"
            @click.prevent="toggleExpandRow($event)"
        >
            <div v-if="staffMember.accessModules.length > 0" class="access-modules">
                <div
                    :title="staffMember.accessModules[0].name"
                    class="access-module access-module_first"
                    v-text="staffMember.accessModules[0].name"
                ></div>
                <div
                    v-for="accessModule in staffMember.accessModules"
                    :key="accessModule.value"
                    :title="accessModule.name"
                    class="access-module"
                    v-text="accessModule.name"
                ></div>
            </div>
            <span
                v-if="staffMember.accessModules.length >= 2"
                class="access-modules-number"
                v-text="`(+${staffMember.accessModules.length - 1})`"
            ></span>
        </td>
        <td
            :class="['comment', { comment_empty: !staffMember.comment }]"
            @click.prevent="toggleExpandRow($event)"
        >
            <p :title="$t('common.clickToExpand')" v-text="staffMember.comment"></p>
        </td>
        <td>
            <div class="actions">
                <template v-if="!editing">
                    <a
                        :class="[
                            'btn-edit',
                            {
                                'super-user': staffIsSuperUser,
                                disabled: editButtonDisabled,
                            },
                        ]"
                        :title="$t('common.edit')"
                        :disabled-text="$t('staff.membersTab.disabledEdit')"
                        @click.prevent="openRow"
                    >
                        <i class="icon-edit-box"></i>
                    </a>
                    <a
                        v-if="trashButtonVisible"
                        :title="$t('common.delete')"
                        :class="[
                            'btn-trash',
                            {
                                'last-assignable-in-assigned-group':
                                    staffMember.userIsLastAssignableInAssignedGroup,
                                disabled: trashButtonDisabled,
                            },
                        ]"
                        :disabled-text="lastAssignableUserErrorText"
                        @click.prevent="remove"
                        @dblclick.stop
                    >
                        <i class="icon-trash"></i>
                    </a>
                </template>
                <template v-else>
                    <a
                        :class="['btn-edit', { disabled: !unsavedChanges }]"
                        :title="$t('common.undo')"
                        @click.prevent="undo"
                    >
                        <i class="icon-arrow-left"></i>
                    </a>
                    <a
                        :class="['btn-edit', { disabled: saveDisabled }]"
                        :title="$t('common.save')"
                        @click.prevent="save"
                    >
                        <i class="icon-checkmark"></i>
                    </a>
                    <a
                        :class="['btn-trash']"
                        :title="$t('common.cancel')"
                        @click.prevent="closeClick"
                        @dblclick.stop
                    >
                        <i class="icon-close"></i>
                    </a>
                </template>
            </div>
        </td>
    </tr>
</template>

<script type="text/babel">
import { mapState, mapGetters } from 'vuex';
import { USER_TYPES } from '../../constants';

export default {
    name: 'StaffRowHeader',

    components: {},

    mixins: [],

    constants: {},

    props: {
        isFormActive: {
            type: Boolean,
            default: false,
        },
        editing: {
            type: Boolean,
            default: false,
        },
        staffMember: {
            type: Object,
            required: true,
        },
        saveDisabled: {
            type: Boolean,
            default: false,
        },
        editClick: {
            type: Function,
            required: true,
        },
        closeClick: {
            type: Function,
            required: true,
        },
        saveClick: {
            type: Function,
            required: true,
        },
        removeClick: {
            type: Function,
            required: true,
        },
        undoClick: {
            type: Function,
            required: true,
        },
    },
    data() {
        return {};
    },

    computed: {
        ...mapGetters({ userType: 'user/userType' }),
        ...mapState({
            unsavedChanges: 'unsavedChanges',
            userId: state => state.user.user.user_id,
        }),

        lastAssignableUserErrorText() {
            let groups = this.staffMember.assignedGroupNames.join(', ');
            return this.$t('staff.membersTab.cannotRemoveLastAssignableStaffFromAssignedGroup', { groups });
        },

        staffIsCurrent() {
            return this.staffMember.user_id === this.userId;
        },

        editButtonDisabled() {
            if (this.isFormActive) {
                return true;
            }

            if (this.loggedUserIsSystemAdmin) {
                return false;
            }

            if (this.staffMember.type === USER_TYPES.SUPER_USER && !this.staffIsCurrent) {
                return true;
            }

            return false;
        },

        trashButtonDisabled() {
            return this.isFormActive || this.staffMember.userIsLastAssignableInAssignedGroup;
        },

        trashButtonVisible() {
            if (this.loggedUserIsSystemAdmin) {
                return true;
            }

            if (this.staffMember.type === USER_TYPES.SUPER_USER) {
                return false;
            }

            if (this.staffMember.type === USER_TYPES.USER_ADMIN && this.staffMember.isAAPrimary) {
                return false;
            }

            return true;
        },

        staffIsUserAdmin() {
            return this.staffMember.type === USER_TYPES.USER_ADMIN;
        },

        staffIsSuperUser() {
            return this.staffMember.type === USER_TYPES.SUPER_USER;
        },

        loggedUserIsSystemAdmin() {
            return this.userType === USER_TYPES.SYS_ADMIN;
        },
    },

    methods: {
        toggleExpandRow(e) {
            if (this.staffMember.accessModules.length > 0 || this.staffMember.comment.length > 0) {
                let row = e.target.closest('.staff-table-row');

                let rows = row.parentNode.parentNode.getElementsByClassName('staff-table-row_expand');

                rows.forEach(item => {
                    if (item !== row) {
                        item.classList.remove('staff-table-row_expand');
                    }
                });

                row.classList.toggle('staff-table-row_expand');
            }
        },

        undo() {
            if (this.unsavedChanges) {
                this.undoClick();
            }
        },

        save() {
            if (!this.saveDisabled) {
                this.saveClick();
            }
        },

        remove() {
            if (!this.trashButtonDisabled) {
                this.removeClick();
            }
        },

        openRow() {
            if (!this.editButtonDisabled) {
                this.editClick();
            }
        },
    },
};
</script>
